import axios, { AxiosResponse } from 'axios'
import { Locale } from './language'

type Service = 'hubSpot' | 'klaviyo'

export interface HubSpotBookADemoPayload {
  formId: string
  consent: boolean
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  company?: string
  turnstileToken: string
}

export interface HubSpotBookADemoResponse {
  error?: string
}

export interface HubSpotNewsletterJoinPayload {
  formId: string
  consent: boolean
  email: string
  name?: string
  turnstileToken: string
}

export interface HubSpotNewsletterJoinResponse {
  error?: string
}

export interface KlaviyoBookADemoPayload {
  listId: string
  email: string
  firstName: string
  lastName: string
  phoneNumber?: string
  company?: string
  turnstileToken: string
}

export interface KlaviyoBookADemoResponse {
  error?: string
}

export interface KlaviyoNewsletterJoinPayload {
  listId: string
  name?: string
  email: string
  turnstileToken: string
}

export interface KlaviyoNewsletterJoinResponse {
  error?: string
}

type BookADemoResponse = KlaviyoBookADemoResponse | HubSpotBookADemoResponse

type NewsletterJoinResponse =
  | KlaviyoNewsletterJoinResponse
  | HubSpotNewsletterJoinResponse

/**
 * Gets URL for an API call.
 */
const getApiUrl = (service: string, action: string) =>
  `/api/${service.toLowerCase()}/${action}`

/**
 * Gets API headers.
 */
const getApiHeaders = (locale: Locale): Record<string, string> => ({
  'Content-Type': 'application/json',
  'X-Locale': locale,
})

/**
 * Makes a request to API endpoint.
 */
const callApi = async <T>(
  url: string,
  payload: object,
  headers: Record<string, string>,
) =>
  await axios.post<T, AxiosResponse<T>, string>(url, JSON.stringify(payload), {
    headers,
  })

/**
 * Calls newsletter signup API page.
 */
export const addEmailToNewsletterList = async (
  locale: Locale,
  service: Service,
  newsletterTargetId: string,
  hasAcceptedTerms: boolean,
  email: string,
  turnstileToken: string,
  name?: string,
) => {
  const url = getApiUrl(service, 'newsletter-join')
  const headers = getApiHeaders(locale)

  if (service === 'klaviyo') {
    const klaviyoNewsletterJoinPayload: KlaviyoNewsletterJoinPayload = {
      listId: newsletterTargetId,
      email,
      name,
      turnstileToken,
    }
    await callApi<NewsletterJoinResponse>(
      url,
      klaviyoNewsletterJoinPayload,
      headers,
    )
  }

  if (service === 'hubSpot') {
    const hubSpotNewsletterJoinPayload: HubSpotNewsletterJoinPayload = {
      formId: newsletterTargetId,
      consent: !!hasAcceptedTerms,
      email,
      name,
      turnstileToken,
    }
    await callApi<NewsletterJoinResponse>(
      url,
      hubSpotNewsletterJoinPayload,
      headers,
    )
  }
}

/**
 * Calls demo booking API page.
 */
export const bookADemo = async (
  locale: Locale,
  service: Service,
  bookingTargetId: string,
  hasAcceptedTerms: boolean,
  email: string,
  firstName: string,
  lastName: string,
  turnstileToken: string,
  phoneNumber?: string,
  company?: string,
) => {
  const url = getApiUrl(service, 'book-a-demo')
  const headers = getApiHeaders(locale)

  if (service === 'klaviyo') {
    const klaviyoBookADemoPayload: KlaviyoBookADemoPayload = {
      listId: bookingTargetId,
      email,
      firstName,
      lastName,
      phoneNumber,
      company,
      turnstileToken,
    }
    await callApi<BookADemoResponse>(url, klaviyoBookADemoPayload, headers)
  }

  if (service === 'hubSpot') {
    const hubSpotBookADemoPayload: HubSpotBookADemoPayload = {
      formId: bookingTargetId,
      consent: !!hasAcceptedTerms,
      email,
      firstName,
      lastName,
      phoneNumber,
      company,
      turnstileToken,
    }
    await callApi<BookADemoResponse>(url, hubSpotBookADemoPayload, headers)
  }
}
